import { Link, Typography } from '@mui/material';
import FlexContainer from 'components/FlexContainer';
import { useAiWriterExpandedSidebarStore } from 'features/aiWriter/AiWriterSidebar/sidebar/aiWriterExpandedSidebarStore';
import { ClaimCardReferencesLoader } from 'features/fact-grabbing/ClaimCardReferencesLoader';
import { ClaimReferencesStatusIcon } from 'features/fact-grabbing/ClaimReferencesStatusIcon';
import { ClaimReferencesStatusText } from 'features/fact-grabbing/ClaimReferencesStatusText';
import { UseReferencesByClaimResult } from 'features/fact-grabbing/useReferencesByClaim';
import FormattedMessage from 'features/i18n/FormattedMessage';
import { MouseEvent } from 'react';
import { ApiClaim } from 'services/backofficeIntegration/http/endpoints/fact-grabbing/httpGetClaimsByText';
import gtmIds from 'services/tracking/GTMIds';
import { withGtmInteraction } from 'services/tracking/withGtmInteraction';
import styled from 'styled-components';
import { withTestId } from 'utils/utils';

type Props = {
  claim: ApiClaim;
  onClick?: (referencesByClaimResultData?: UseReferencesByClaimResult) => void;
  showReviewButton?: boolean;
  isClickable?: boolean;
};

export const ClaimCard = ({
  claim,
  onClick,
  showReviewButton = true,
  isClickable = true
}: Props) => {
  const isSidebarExpanded = useAiWriterExpandedSidebarStore(state => state.isSidebarExpanded);

  const createCardClickHandler =
    (referencesByClaimResultData?: UseReferencesByClaimResult) => (event: MouseEvent) => {
      if (!isClickable || !onClick) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      onClick(referencesByClaimResultData);
    };

  return (
    <ClaimCardReferencesLoader claim={claim}>
      {({
        judgmentCounts,
        isLoading: isLoadingReferences,
        isError: isReferencesError,
        referencesByClaimResultData
      }) => {
        const handleOnClick = createCardClickHandler(referencesByClaimResultData);

        return (
          <Root
            {...withGtmInteraction(gtmIds.aiWriter.factGrabbing.claimCard)}
            {...withTestId('check-claims-card')}
            onClick={handleOnClick}
            $isClickable={isClickable}
          >
            <StatusIconContainer {...withTestId('check-claims-status-icon')}>
              <ClaimReferencesStatusIcon
                judgmentCounts={judgmentCounts}
                isLoading={isLoadingReferences}
                isError={isReferencesError}
              />
            </StatusIconContainer>

            <FlexContainer direction="column" gap="two" fullWidth={true}>
              <FlexContainer direction="column" fullWidth={true}>
                <MultilineTruncateTypography
                  {...withTestId('check-claims-text')}
                  variant="subtitle1"
                  fontWeight="bold"
                  maxWidth={isSidebarExpanded ? 550 : 350}
                  $lines={2}
                  title={claim.claim}
                >
                  {claim.claim}
                </MultilineTruncateTypography>

                <MultilineTruncateTypography
                  {...withTestId('check-claims-reference')}
                  variant="body2"
                  $lines={3}
                  title={claim.reference}
                >
                  &quot;{claim.reference}&quot;
                </MultilineTruncateTypography>
              </FlexContainer>

              <FlexContainer direction="column" gap={0}>
                <ClaimReferencesStatusText
                  judgmentCounts={judgmentCounts}
                  isLoading={isLoadingReferences}
                  isError={isReferencesError}
                />

                {showReviewButton && (
                  <ReviewContainer {...withTestId('check-claims-review-container')}>
                    <Link
                      {...withGtmInteraction(gtmIds.aiWriter.factGrabbing.claimCard)}
                      {...withTestId('check-claims-review-link')}
                      href="#"
                      onClick={handleOnClick}
                      
                    >
                      <FormattedMessage id="aiWriter.inspirations.factGrabbing.claim.review_button.label" />
                    </Link>
                  </ReviewContainer>
                )}
              </FlexContainer>
            </FlexContainer>
          </Root>
        );
      }}
    </ClaimCardReferencesLoader>
  );
};

const ReviewContainer = styled.div`
  margin-left: auto;
  transition: opacity 0.2s ease-in-out;

  opacity: 0;
`;

const Root = styled(FlexContainer).attrs({
  direction: 'row',
  gap: 'three'
})<{ $isClickable: boolean }>`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacings.three} ${theme.spacings.four}`};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.borderRadius.one};
  cursor: ${props => (props.$isClickable ? 'pointer' : 'default')};
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:hover {
    border-color: ${({ theme, $isClickable }) =>
      $isClickable ? theme.colors.componentsInputfieldOutlinedEnabledborder : undefined};
    box-shadow: ${({ theme, $isClickable }) => ($isClickable ? theme.shadow.card : undefined)};

    ${ReviewContainer} {
      opacity: ${props => (props.$isClickable ? 1 : 0)};
    }
  }
`;

const StatusIconContainer = styled.div``;

const MultilineTruncateTypography = styled(Typography)<{ $lines: number }>`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: ${({ $lines }) => $lines};
  -webkit-box-orient: vertical;
  max-width: 100%;
`;
